(function () {
  const MOBILE = window.matchMedia("(max-width: 749px)");

  class CustomersSayCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector(".cst-viewport");
      this.track = this.querySelector(".cst-track");
      this.dotsHost = this.querySelector(".cst-dots");
      this.controls = this.querySelector(".cst-controls");
      this.realCards = [...this.querySelectorAll(".cst-card")];
      this.count = this.realCards.length;
      this.index = 0;
      this.clones = 0;
      this.animating = false;
      this.timer = null;
      this.resumeTimer = null;
      this.built = false;

      this.intervalMs =
        (parseInt(this.dataset.interval, 10) || 6) * 1000;
      this.pauseMs = (parseInt(this.dataset.pause, 10) || 4) * 1000;
      this.autoplayRequested = this.dataset.autoplay === "true";
      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      this.onTransitionEnd = () => this.snapIfClone();
      this.track.addEventListener("transitionend", this.onTransitionEnd);

      this.bindNav();
      this.onResize = () => this.layout();
      MOBILE.addEventListener("change", this.onResize);
      window.addEventListener("resize", this.onResize);
      this.layout();
    }

    disconnectedCallback() {
      MOBILE.removeEventListener("change", this.onResize);
      window.removeEventListener("resize", this.onResize);
      this.track.removeEventListener("transitionend", this.onTransitionEnd);
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
    }

    itemsPerPage() {
      return MOBILE.matches ? 1 : 3;
    }

    carouselMode() {
      return this.count > 3;
    }

    autoplayEnabled() {
      return (
        this.carouselMode() &&
        this.autoplayRequested &&
        !this.reducedMotion
      );
    }

    setPerPageCss() {
      const per = this.itemsPerPage();
      this.style.setProperty("--cst-per-page", String(per));
    }

    clearClones() {
      this.track
        .querySelectorAll(".cst-card.is-clone")
        .forEach((node) => node.remove());
      this.clones = 0;
      this.built = false;
    }

    buildTrack() {
      this.clearClones();
      this.realCards = [...this.track.querySelectorAll(".cst-card:not(.is-clone)")];
      this.count = this.realCards.length;
      if (!this.carouselMode()) return;

      const n = this.itemsPerPage();
      this.clones = n;

      for (let i = this.count - n; i < this.count; i++) {
        const clone = this.realCards[i].cloneNode(true);
        clone.classList.add("is-clone");
        clone.setAttribute("aria-hidden", "true");
        this.track.insertBefore(clone, this.track.firstChild);
      }

      for (let i = 0; i < n; i++) {
        const clone = this.realCards[i].cloneNode(true);
        clone.classList.add("is-clone");
        clone.setAttribute("aria-hidden", "true");
        this.track.appendChild(clone);
      }

      this.index = n;
      this.built = true;
    }

    cardWidth() {
      const first = this.track.querySelector(".cst-card");
      return first ? first.getBoundingClientRect().width : 0;
    }

    bindNav() {
      this.querySelectorAll("[data-direction]").forEach((btn) => {
        btn.addEventListener("click", () => {
          this.go(btn.dataset.direction === "next" ? 1 : -1, true);
        });
      });
    }

    realIndex() {
      if (!this.carouselMode() || !this.built) return this.index;
      const n = this.clones;
      let i = this.index - n;
      while (i < 0) i += this.count;
      while (i >= this.count) i -= this.count;
      return i;
    }

    buildDots() {
      if (!this.dotsHost) return;
      this.dotsHost.innerHTML = "";
      if (!this.carouselMode() || this.count <= 1) return;

      for (let i = 0; i < this.count; i++) {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "cst-dot" + (i === this.realIndex() ? " is-active" : "");
        dot.setAttribute("aria-label", "Go to testimonial " + (i + 1));
        const target = i;
        dot.addEventListener("click", () => {
          this.goToReal(target, true);
        });
        this.dotsHost.appendChild(dot);
      }
    }

    syncDots() {
      if (!this.dotsHost) return;
      const active = this.realIndex();
      [...this.dotsHost.children].forEach((dot, i) => {
        dot.classList.toggle("is-active", i === active);
      });
    }

    setTransition(on) {
      this.track.style.transition = on ? "" : "none";
    }

    applyTransform() {
      const w = this.cardWidth();
      this.track.style.transform = "translateX(-" + this.index * w + "px)";
    }

    snapIfClone() {
      if (!this.carouselMode() || !this.built) {
        this.animating = false;
        return;
      }
      const n = this.clones;
      let snapped = false;

      if (this.index >= n + this.count) {
        this.setTransition(false);
        this.index = this.index - this.count;
        this.applyTransform();
        snapped = true;
      } else if (this.index < n) {
        this.setTransition(false);
        this.index = this.index + this.count;
        this.applyTransform();
        snapped = true;
      }

      if (snapped) {
        void this.track.offsetHeight;
        this.setTransition(true);
      }

      this.animating = false;
      this.syncDots();
    }

    go(delta, manual) {
      if (!this.carouselMode() || this.animating) return;
      this.animating = true;
      this.index += delta;
      this.setTransition(true);
      this.applyTransform();
      this.syncDots();
      if (manual) this.pauseThenResume();
    }

    goToReal(realTarget, manual) {
      if (!this.carouselMode()) return;
      const current = this.realIndex();
      if (realTarget === current) return;
      const n = this.clones;
      this.animating = true;
      this.index = n + realTarget;
      this.setTransition(true);
      this.applyTransform();
      this.syncDots();
      if (manual) this.pauseThenResume();
    }

    layout() {
      this.setPerPageCss();
      const carousel = this.carouselMode();

      if (this.controls) {
        this.controls.hidden = !carousel;
      }

      if (!carousel) {
        this.clearClones();
        this.track.classList.add("is-static");
        this.track.style.transform = "";
        this.index = 0;
        this.stopAutoplay();
        if (this.dotsHost) this.dotsHost.innerHTML = "";
        return;
      }

      this.track.classList.remove("is-static");
      this.buildTrack();
      this.setTransition(false);
      this.applyTransform();
      void this.track.offsetHeight;
      this.setTransition(true);
      this.buildDots();
      this.syncDots();
      this.animating = false;
      this.startAutoplay();
    }

    pauseThenResume() {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      if (!this.autoplayEnabled()) return;
      this.resumeTimer = setTimeout(() => this.startAutoplay(), this.pauseMs);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled()) return;
      this.timer = setInterval(() => this.go(1, false), this.intervalMs);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  if (!customElements.get("customers-say-carousel")) {
    customElements.define("customers-say-carousel", CustomersSayCarousel);
  }
})();
